import { EventEmitter, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, finalize, map, shareReplay, take, tap } from 'rxjs/operators';

import { AuthVerifyI, CurrentUser, UserOrganization } from '@qiasphere/interfaces';

import { AuthStatus } from '../enums/auth-status.enum';
import { OrganizationRoleEnum } from '../enums/organization-roles.enum';
import { SessionStorageEnum } from '../enums/session-storage.enum';
import { UserAccessRight } from '../enums/user-access-right.enum';
import { CasService } from './cas.service';
import { ClientConfigService } from './client-config.service';
import { MonitoringService } from './monitoring/monitoring.service';
import { ServiceAccountAuthService } from './service-account-auth.service';
import { SessionStorageService } from './session-storage.service';
import { WINDOW } from './window.service';

/**
 * Central authentication facade that abstracts away the difference between
 * human (CAS) and machine (service-account) authentication flows.
 *
 * Exists as a separate service because the portal supports two distinct login
 * paths -- browser-based CAS SSO and programmatic service-account tokens --
 * and consumers should not need to know which one is active. This service
 * also owns the "active organization" concept, which gates API base URLs,
 * route guards, and terms-and-conditions enforcement across the entire app.
 *
 * Key integration points: CasService, ServiceAccountAuthService,
 * SessionStorageService (persists active org across page reloads),
 * ClientConfigService (resolves environment-specific API URLs).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly window = inject(WINDOW);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly cas = inject(CasService);
  private readonly saa = inject(ServiceAccountAuthService);
  private readonly monitoring = inject(MonitoringService);

  private verifyInFlight$: Observable<AuthVerifyI> | null = null;

  jwt: string;
  status$ = new BehaviorSubject<AuthStatus | null>(null);
  activeOrganization$ = new BehaviorSubject<UserOrganization | null>(this.activeOrganization);
  reloadUser: EventEmitter<null> = new EventEmitter<null>();

  get authService() {
    return this.isServiceUser ? this.saa : this.cas;
  }

  get user$(): BehaviorSubject<CurrentUser> {
    return this.authService.user$;
  }

  get userOrganizations(): UserOrganization[] {
    return this.user ? this.user.userOrganizations : ([] as UserOrganization[]);
  }

  get activeOrganization(): UserOrganization {
    const data = this.sessionStorageService.getItem(SessionStorageEnum.qs_activeOrganization);
    if (data && !this.isValidUserOrganization(data)) {
      console.error('SessionStorage: invalid activeOrganization data, clearing');
      this.sessionStorageService.removeItem(SessionStorageEnum.qs_activeOrganization);
      return null;
    }
    return data;
  }

  /**
   * Persists the selected organization to session storage and broadcasts
   * the change so that route guards, API URL builders, and UI components
   * react to the new organizational context. Skips the update when the
   * value has not actually changed to avoid unnecessary re-renders.
   */
  set activeOrganization(organization: UserOrganization) {
    const savedOrganization = this.sessionStorageService.getItem(SessionStorageEnum.qs_activeOrganization);
    if (JSON.stringify(savedOrganization) !== JSON.stringify(organization)) {
      this.sessionStorageService.setItem(SessionStorageEnum.qs_activeOrganization, organization);
      this.activeOrganization$.next(organization);
      this.updateAuthStatus();
    }
  }

  /**
   * True when the app is running under the "/service" path prefix, selecting
   * the Azure AD / MSAL service-account auth flow instead of CAS. The path
   * prefix doesn't change during in-app navigation, so the value is stable
   * for the session -- no caching needed. Hash check is defensive against
   * legacy hash-style URLs that might still be bookmarked.
   */
  get isServiceUser(): boolean {
    const path = this.window.location.pathname;
    const hash = this.window.location.hash;
    return path.startsWith('/service') || hash.startsWith('#/service');
  }

  /**
   * Reactive admin check that also requires T&C approval --
   * used by templates that need to hide admin-only UI until the
   * user has completed the full onboarding flow.
   */
  get isAdmin$(): Observable<boolean> {
    return this.activeOrganization$.pipe(
      map(organization => {
        return (
          organization &&
          (organization.roles?.indexOf(OrganizationRoleEnum.ADMIN) !== -1 ||
            organization.roles?.indexOf(OrganizationRoleEnum.SERVICE_ACCOUNT_ADMIN) !== -1) &&
          organization.termsAndConditionsApproved
        );
      })
    );
  }

  get isAdmin(): boolean {
    const status = this.getAuthStatus();
    return status === AuthStatus.ADMIN || status === AuthStatus.SERVICE;
  }

  private get user(): CurrentUser | null {
    return this.authService.user$.value;
  }

  /**
   * Only returns a bearer token for service-account sessions; human CAS
   * sessions rely on HTTP-only cookies, so this intentionally emits null.
   */
  getAuthBearer(): Observable<string> {
    return this.isServiceUser ? this.saa.getToken().pipe(map(r => r.idToken)) : of(null);
  }

  updateDepartmentOfActiveOrganization(department: string) {
    const currentActiveOrganization = this.userOrganizations.find(
      org => org.organization.id === this.activeOrganization.organization.id
    );
    currentActiveOrganization.organization.department = department;
    this.activeOrganization = currentActiveOrganization;
  }

  /**
   * Bootstrap entry point called once at app startup. Delegates to
   * the active auth provider to resolve the current user from a CAS
   * ticket or service-account token. Always publishes a non-null
   * `status$` value on completion. Concurrent callers share a single
   * in-flight `/current-user` request via shareReplay; the cache is
   * released on completion or error so subsequent explicit verifies
   * (e.g. after license submission) refetch fresh.
   */
  verify(): Observable<AuthVerifyI> {
    if (this.verifyInFlight$) {
      return this.verifyInFlight$;
    }
    this.verifyInFlight$ = this.authService.verify(this.jwt).pipe(
      filter((r: AuthVerifyI | null) => r != null),
      take(1),
      tap(({ user }) => {
        if (user) {
          this.initActiveOrganization();
          this.monitoring.setAuthenticatedUser(user.qiagenUserId, this.activeOrganization?.organization?.id);
        }
        this.updateAuthStatus();
      }),
      finalize(() => {
        this.verifyInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.verifyInFlight$;
  }

  /**
   * Constructs the organization-scoped API base URL that all data
   * services prepend to their HTTP calls. By default it blocks until
   * the active organization has accepted T&C, preventing premature
   * API calls that would be rejected by the backend.
   *
   * @param tacCheck Set to false for endpoints that must work before T&C acceptance (e.g. the T&C endpoint itself).
   * @param orgId Supply directly to bypass the active-organization observable (used during org-switching).
   */
  getBaseUrl(tacCheck = true, orgId?: string): Observable<string> {
    const basePath$ = this.isServiceUser ? this.clientConfigService.ssoUrl() : this.clientConfigService.apiUrl();

    const organizationId$ = orgId
      ? of(orgId)
      : this.activeOrganization$
          .pipe(
            filter((activeOrganization: UserOrganization) => {
              return activeOrganization !== null && (!tacCheck || activeOrganization.termsAndConditionsApproved);
            })
          )
          .pipe(
            map((e: UserOrganization) => {
              return e.organization.id;
            })
          );
    return combineLatest([basePath$, organizationId$]).pipe(
      take(1),
      map(([basePath, organizationId]) => {
        return `${basePath}/organization/${organizationId}`;
      })
    );
  }

  /**
   * Triggers re-evaluation of organization selection and T&C flow in
   * app.component, typically after the user profile changes server-side.
   * Also cleans the URL so activation tokens don't persist across navigations.
   */
  emitReloadUserEvent(): void {
    this.setBasePath();
    this.reloadUser.emit();
  }

  /**
   * Guards features that require T&C acceptance for the active organization.
   * Returns false both when T&C are pending and when no organization is selected.
   */
  hasAcceptedTAC(): boolean {
    return this.activeOrganization.termsAndConditionsApproved;
  }

  /**
   * Strips query parameters (tickets, activation tokens) from the browser
   * URL after they have been consumed, so they don't leak into bookmarks
   * or cause duplicate authentication attempts on refresh.
   */
  setBasePath(): void {
    const query = `${this.window.location.origin + this.window.location.pathname}`;
    this.window.history.replaceState({}, document.title, query);
  }

  redirectToLogin(): void {
    this.authService.redirectToLogin();
  }

  /**
   * Derives the coarse-grained authorization level used by route guards.
   * Service users bypass org/role checks entirely; human users are
   * classified as NON_QIASPHERE_USER only when they lack a valid org/role
   * (not yet licensed). Pending T&C acceptance is handled separately by the
   * TAC overlay and must not affect this status, or it would incorrectly
   * route already-licensed admins/users into the license-request flow.
   */
  getAuthStatus(): AuthStatus {
    if (this.isServiceUser) {
      return AuthStatus.SERVICE;
    }
    const org = this.activeOrganization;
    if (!org?.roles) {
      return AuthStatus.NON_QIASPHERE_USER;
    }
    if (
      org.roles.includes(OrganizationRoleEnum.ADMIN) ||
      org.roles.includes(OrganizationRoleEnum.SERVICE_ACCOUNT_ADMIN)
    ) {
      return AuthStatus.ADMIN;
    }
    return AuthStatus.USER;
  }

  /**
   * Reconciles the session-stored active organization with the fresh
   * user profile from the backend, so that renamed orgs or revoked
   * memberships are reflected immediately after login.
   */
  private initActiveOrganization(): void {
    if (!this.user) return;
    const savedOrg = this.sessionStorageService.getItem(SessionStorageEnum.qs_activeOrganization);
    const fallbackOrg = this.isServiceUser ? null : this.user.userOrganizations[0] || null;
    const matchingOrg = savedOrg
      ? this.user.userOrganizations.find(o => o.organization.id === savedOrg.organization.id)
      : null;
    this.activeOrganization = matchingOrg || fallbackOrg;
  }

  /**
   * Validates the *structural* shape of data read from sessionStorage to
   * detect corruption. Only checks fields that downstream code requires to
   * function (organization.id is the matching key); other fields may be
   * legitimately missing -- e.g. service-user payloads from /current-user
   * may omit termsAndConditionsApproved or accessRights, and JSON.stringify
   * drops undefined values during the sessionStorage round-trip. Insisting
   * those fields exist caused the active organization to be cleared on
   * every read, which then made initActiveOrganization fall back to
   * userOrganizations[0] -- the "always resets to default org" bug.
   */
  private isValidUserOrganization(data: any): data is UserOrganization {
    return (
      data != null &&
      typeof data === 'object' &&
      data.organization != null &&
      typeof data.organization === 'object' &&
      typeof data.organization.id === 'string'
    );
  }

  /** Broadcasts to route guards only when the status actually changes. */
  private updateAuthStatus(): void {
    const newStatus = this.getAuthStatus();
    if (this.status$.value !== newStatus) {
      this.status$.next(newStatus);
    }
  }

  /**
   * Fine-grained permission check against the active organization's
   * access-rights list, used to conditionally enable UI features
   * (e.g. instrument remote configuration, protocol management).
   */
  public hasAccessRight(accessRight: UserAccessRight): boolean {
    return this.activeOrganization?.accessRights?.includes(accessRight) ?? false;
  }
}
